import { useState, useEffect, useCallback, Suspense } from 'react'
import { Loader2, X, RotateCcw, AlertCircle, Clock, Eye, Code, Pencil, Download } from 'lucide-react'
import { useConfirm, useToast } from '@papan/core-ui/ui'
import { sessionSignal } from '@papan/core-ui/ui/sessionEnded'
import { filesApi, type FileVersion } from '@papan/files/client'
import { fetchAndCacheFileInfo } from '@papan/files/client/queries'
import { useQueryClient } from '@tanstack/react-query'
import { getPreviewForExtension, getEditorForExtension } from '@papan/collab/client'
import { ModeToggle } from '@papan/collab/ui'
import { versionPreviewSize } from './versionPreviewSize.js'
import { restoreErrorText } from './restoreErrorText'
import { errorText } from '@papan/api-client'

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

function formatVersionDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface VersionPreviewProps {
  fileId: string
  versionId: string
  fileName?: string
  onBack?: () => void
  /** Persisted view mode for text editors (preview/source) */
  viewMode?: 'preview' | 'source'
  onViewModeChange?: (mode: 'preview' | 'source') => void
  /** Shared scroll position map (keyed by fileId) */
  scrollRef?: Record<string, number>
  viewSync?: Record<string, Record<string, unknown>>
  /** Whether this is the latest (current) version */
  isLatest?: boolean
  /** Whether the current file has unsaved changes */
  isDirty?: boolean
  /** Callback to discard unsaved changes (used when latest version is selected) */
  onDiscard?: () => void
  /** Hide the per-file Restore/Discard buttons — used when this preview backs
   *  an APP-version selection (the whole-app restore lives on the App history
   *  panel's own bar; restoring just this one file here would be wrong). The
   *  view toggle and Close button stay. */
  hideActions?: boolean
  /** Override the `v{N}` label in the version badge — used by the app-version
   *  preview so the badge shows the APP-version number the panel labelled
   *  (e.g. `v3`), not `index.html`'s own file-version number (which differs). */
  versionLabelOverride?: string
}

/**
 * Read-only version preview component.
 *
 * Rendered inline in the workspace when previewing a specific version
 * from the VersionPanel. Shows a version banner with "Viewing v{N} from [date]"
 * and "Restore" / "Back" buttons. Hides save/discard/dirty UI.
 */
export function VersionPreview({ fileId, versionId, fileName: propFileName, onBack, viewMode, onViewModeChange, scrollRef, viewSync, isLatest, isDirty, onDiscard, hideActions, versionLabelOverride }: VersionPreviewProps) {
  const confirm = useConfirm()
  const toast = useToast()

  const queryClient = useQueryClient()
  const [fileName, setFileName] = useState(propFileName || null)
  const [fileExt, setFileExt] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [versions, setVersions] = useState<FileVersion[]>([])
  const [restoring, setRestoring] = useState(false)
  const [isBinary, setIsBinary] = useState(false)
  // #1316: a dropped answer (its session ended) is asked again as the session now here — never shown as an error
  const [reask, setReask] = useState(0)

  // Load file info and versions
  useEffect(() => {
    setLoading(true)
    setError(null)

    const infoPromise = propFileName
      ? Promise.resolve({ name: propFileName })
      : fetchAndCacheFileInfo(queryClient, fileId)

    Promise.all([
      infoPromise,
      filesApi.getVersions(fileId),
    ])
      .then(([info, versResult]) => {
        const name = info.name
        setFileName(name)
        const ext = name.split('.').pop()?.toLowerCase() || ''
        setFileExt(ext)
        setVersions(versResult.versions)

        // Check binary status for unknown file types (no registered editor/preview).
        // The propFileName branch above skipped the detail fetch, so we still need
        // it for `isBinary`. The cache helper makes this a hit when we already
        // fetched detail above.
        const hasEditor = getEditorForExtension(ext)
        const hasPreview = getPreviewForExtension(ext)
        if (!hasEditor && !hasPreview) {
          fetchAndCacheFileInfo(queryClient, fileId)
            .then(full => { setIsBinary(full.isBinary ?? false); setLoading(false) })
            .catch(() => setLoading(false))
        } else {
          setLoading(false)
        }
      })
      .catch(err => {
        const text = errorText(err, 'Failed to load file')
        if (text === null) { setReask(n => n + 1); return }
        setError(text)
        setLoading(false)
      })
  }, [fileId, propFileName, queryClient, reask])

  // Find version info for display
  const versionInfo = versions.find(v => v.id === versionId) || null
  const versionNumber = versionInfo?.versionNumber ?? null
  // #981: a version the list does not hold still gets a size (the file detail's) — the viewer picks the windowed
  // large-CSV grid by it, never reading a large file whole.
  const [detailSize, setDetailSize] = useState<number | null | undefined>(undefined)
  const needDetailSize = !loading && !error && !versionInfo
  useEffect(() => {
    if (!needDetailSize) return
    let live = true
    fetchAndCacheFileInfo(queryClient, fileId)
      .then(full => { if (live) setDetailSize(full.size ?? null) })
      .catch(() => { if (live) setDetailSize(null) })
    return () => { live = false }
  }, [needDetailSize, queryClient, fileId])
  const previewSize = versionPreviewSize(versionInfo?.size, detailSize)

  const handleRestore = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Restore Version',
      message: 'This will replace the current content with the selected version. Any unsaved changes will be lost.',
      type: 'warning',
      confirmText: 'Restore',
      cancelText: 'Cancel',
    })
    if (!confirmed) return

    // E7 PR 0b (D134): the restore runs to completion; only what it SHOWS is dropped once the session ended
    // (no toast, no state change, no navigation: the panel may be a kept-alive one of the old account)
    const signal = sessionSignal()
    setRestoring(true)
    try {
      const restored = await filesApi.restoreVersion(fileId, versionId)
      if (signal.aborted) return
      // #1090 — a large app database is restored in the background; its outcome arrives as a notice.
      toast.success(restored?.queued ? 'Restoring the database in the background — you will be notified when it is done' : 'Version restored successfully')
      onBack?.()
    } catch (err) {
      if (signal.aborted) return
      // #1090 / #1134 — the db is being replaced (or a save is still landing); #1397 — a damaged stored copy: the
      // server's own words say which
      toast.error(restoreErrorText(err, 'Failed to restore version'))
      console.error('[VersionPreview] Restore failed:', err)
    } finally {
      if (!signal.aborted) setRestoring(false)
    }
  }, [fileId, versionId, confirm, toast, onBack])

  if (loading) {
    return LoadingSpinner
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm">{error}</p>
          <button onClick={onBack} className="mt-3 text-xs themed-btn-secondary px-3 py-1 rounded">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Determine what to render:
  // 1. Preview registry FIRST (dedicated read-only viewers — CollabDocViewer)
  // 2. Editor registry (editors that handle versionId natively in readonly mode)
  // 3. Read-only editors (images, video — they ARE the viewer)
  let PreviewComponent: React.ComponentType<any> | null = null
  let supportsVersionRefresh = false

  const previewReg = getPreviewForExtension(fileExt)
  if (previewReg) {
    PreviewComponent = previewReg.component
    supportsVersionRefresh = previewReg.supportsVersionRefresh ?? false
  }

  const editorReg = getEditorForExtension(fileExt)
  if (!PreviewComponent && editorReg && !editorReg.readOnly) {
    // Full editor — will receive versionId and render in readonly mode
    PreviewComponent = editorReg.component
  }

  if (!PreviewComponent) {
    if (editorReg?.readOnly) {
      PreviewComponent = editorReg.component
    }
  }

  // 4. Fall back to text preview for unknown file types (read-only, no collab connection needed)
  //    Skip binary files — they can't be meaningfully displayed as text
  if (!PreviewComponent && !isBinary) {
    const txtPreview = getPreviewForExtension('txt')
    if (txtPreview) PreviewComponent = txtPreview.component
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Version banner */}
      {versionInfo && (() => {
        const ext = (fileName || '').split('.').pop()?.toLowerCase() || ''
        const hasViewToggle = /\.(md|html|htm|csv|tsv)$/i.test(fileName || '')
        const isCsv = ext === 'csv' || ext === 'tsv'
        return (
          <div className="h-10 px-4 flex items-center gap-2 themed-bg-secondary border-b themed-border shrink-0">
            {hasViewToggle && (
              <ModeToggle
                options={[
                  { value: 'preview', label: isCsv ? 'View' : 'Preview', icon: <Eye size={14} /> },
                  { value: 'source', label: isCsv ? 'Edit' : 'Source', icon: isCsv ? <Pencil size={14} /> : <Code size={14} /> },
                ]}
                value={viewMode === 'source' ? 'source' : 'preview'}
                onChange={m => onViewModeChange?.(m)}
              />
            )}
            <div className="flex-1" />
            {!hideActions && (isLatest ? (
              <button
                onClick={() => { onDiscard?.(); onBack?.() }}
                disabled={!isDirty}
                className="flex items-center gap-1.5 px-3 py-1 text-xs rounded
                  bg-amber-600 hover:bg-amber-500 text-white transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw size={12} />
                <span>Discard Changes</span>
              </button>
            ) : (
              <button
                onClick={handleRestore}
                disabled={restoring}
                className="flex items-center gap-1.5 px-3 py-1 text-xs rounded
                  bg-amber-600 hover:bg-amber-500 text-white transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {restoring ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RotateCcw size={12} />
                )}
                <span>{restoring ? 'Restoring...' : 'Restore'}</span>
              </button>
            ))}
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 px-3 py-1 text-xs rounded themed-btn-secondary transition-colors"
              >
                <X size={12} />
                <span>Close</span>
              </button>
            )}
          </div>
        )
      })()}

      {/* Content with floating version badge */}
      {PreviewComponent && previewSize === undefined ? LoadingSpinner : PreviewComponent ? (
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <Suspense fallback={LoadingSpinner}>
            <PreviewComponent
              key={supportsVersionRefresh ? undefined : versionId}
              fileId={fileId}
              fileName={fileName}
              versionId={versionId}
              size={previewSize ?? null}
              viewMode={viewMode}
              initialViewMode={viewMode}
              onViewModeChange={onViewModeChange}
              scrollRef={scrollRef}
              viewSync={viewSync}
            />
          </Suspense>
          {versionInfo && (
            // Top-RIGHT, because every viewer's CONTENT is left-aligned — a
            // markdown preview renders no toolbar here and starts its first
            // heading at the top-left, which this would print over. A viewer
            // that puts controls in this corner steps down out of the way
            // instead (ImageViewer, keyed on `versionId`).
            <div className="absolute top-1 right-4 flex items-center gap-2 px-3 py-1.5 rounded-lg border themed-border themed-bg-secondary text-white text-sm pointer-events-none z-10" style={{ opacity: 0.8 }}>
              <Clock size={14} />
              {versionLabelOverride ?? `v${versionNumber}`} &middot; {formatVersionDate(versionInfo.createdAt)}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center themed-text-muted">
          <div className="text-center">
            <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p>Preview not available for .{fileExt} files</p>
            {/* Version-preview download targets the current file (filesApi.download
                doesn't take versionId today). Restore-then-download is the path
                for getting THIS specific version's bytes — surface that as a
                secondary action via "Restore" (the existing version-history UI). */}
            <div className="mt-3 flex items-center justify-center gap-2">
              <button
                onClick={() => filesApi.download([fileId])}
                className="text-xs themed-btn-primary px-3 py-1 rounded inline-flex items-center gap-1.5"
              >
                <Download size={12} />
                Download current
              </button>
              <button onClick={onBack} className="text-xs themed-btn-secondary px-3 py-1 rounded">
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
